// Copyright (c) Mysten Labs, Inc. — DGC: pool stake labels + bigint-safe amounts
// SPDX-License-Identifier: Apache-2.0

import { type SuiValidatorSummary } from '@mysten/sui.js/client';
import { Heading } from '@mysten/ui';

import { DelegationAmount } from './DelegationAmount';
import { Card } from '~/ui/Card';
import { Stats } from '~/ui/Stats';

type StatsCardProps = {
	validatorData: SuiValidatorSummary;
	epoch: number | string;
	epochRewards: number | null;
	apy: number | string | null;
	tallyingScore: string | null;
};

/** Genesis self-stake per validator (whitepaper §5). */
const GENESIS_SELF_STAKE_MIST = 2_000_000_000_000_000n;

export function ValidatorStats({
	validatorData,
	epochRewards,
	apy,
	tallyingScore,
}: StatsCardProps) {
	const poolTotalMist = BigInt(validatorData.stakingPoolSuiBalance ?? 0);
	const rewardsPoolMist = BigInt(validatorData.rewardsPool ?? 0);
	const principalEstimateMist =
		poolTotalMist > rewardsPoolMist ? poolTotalMist - rewardsPoolMist : poolTotalMist;

	const commission = Number(validatorData.commissionRate) / 100;

	return (
		<div className="flex flex-col items-stretch gap-5 md:flex-row">
			<div className="flex-grow">
				<Card spacing="lg" height="full" bg="dark" growOnHover>
					<div className="flex basis-full flex-col gap-8 md:basis-1/3">
						<Heading as="div" variant="heading4/semibold" color="dark">
							RWAX in staking pool
						</Heading>
						<div className="flex flex-col gap-8 lg:flex-row">
							<Stats
								label="Staking APY"
								tooltip="Annualized yield from this validator's recent epochs (estimate)."
								unavailable={apy === null}
								color="dark"
							>
								{apy}%
							</Stats>
							<Stats
								label="Pool total (incl. rewards)"
								tooltip="stakingPoolSuiBalance: validator pool principal plus rewards already deposited into the pool (Move sui_balance includes rewards_pool)."
								unavailable={poolTotalMist <= 0n}
								color="dark"
							>
								<DelegationAmount amount={poolTotalMist} isStats />
							</Stats>
						</div>
						<div className="flex flex-col gap-8 lg:flex-row">
							<Stats
								label="Est. principal (~genesis + delegations)"
								tooltip="Roughly pool total minus rewards_pool; expect ~2M RWAX right after genesis before subsidies accrue."
								color="dark"
								unavailable={principalEstimateMist <= 0n}
							>
								<DelegationAmount amount={principalEstimateMist} isStats />
							</Stats>
							<Stats
								label="Genesis self-stake (spec)"
								tooltip="Whitepaper allocations: 2,000,000 RWAX per genesis validator."
								color="dark"
							>
								<DelegationAmount amount={GENESIS_SELF_STAKE_MIST} isStats />
							</Stats>
						</div>
						<div className="flex flex-col gap-8 lg:flex-row">
							<Stats label="Commission" tooltip="Validator commission on staking rewards." color="dark">
								<Heading as="h3" variant="heading2/semibold" color="green">
									{commission}%
								</Heading>
							</Stats>
							<Stats label="Delegators" tooltip="Active delegator count (not indexed on local RWAX)." unavailable color="dark"/>
						</div>
					</div>
				</Card>
			</div>

			<div className="flex-grow">
				<Card spacing="lg" height="full" bg="dark" growOnHover>
					<div className="flex basis-full flex-col items-stretch gap-8 md:basis-80">
						<Heading as="div" variant="heading4/semibold" color="dark">
							Validator staking rewards
						</Heading>
						<div className="flex flex-col gap-8">
							<Stats
								label="Last epoch rewards"
								tooltip="Rewards credited in the previous epoch (event index)."
								color="dark"
								unavailable={epochRewards === null}
							>
								<DelegationAmount
									amount={typeof epochRewards === 'number' ? epochRewards : 0n}
									isStats
								/>
							</Stats>

							<Stats
								label="Rewards pool balance"
								tooltip="RWAX sitting in rewards_pool (subset of pool total; grows with stake subsidy each epoch)."
								color="dark"
								unavailable={rewardsPoolMist <= 0n}
							>
								<DelegationAmount amount={rewardsPoolMist} isStats />
							</Stats>
						</div>
					</div>
				</Card>
			</div>

			<div className="flex-grow">
				<Card spacing="lg" height="full" bg="dark" growOnHover>
					<div className="flex max-w-full flex-col gap-8">
						<Heading as="div" variant="heading4/semibold" color="dark">
							Network participation
						</Heading>
						<div className="flex flex-col gap-8">
							<div className="flex flex-col gap-8 lg:flex-row">
								<Stats label="Checkpoint participation" unavailable color="dark"/>
								<Stats label="Voted last round" unavailable color="dark"/>
							</div>
							<div className="flex flex-col gap-8 lg:flex-row">
								<Stats label="Tallying score" unavailable={!tallyingScore} color="dark">
									{tallyingScore}
								</Stats>
								<Stats label="Last Narwhal round" unavailable color="dark"/>
								<Stats label="Proposed next epoch gas price" color="dark">
									<DelegationAmount amount={validatorData.nextEpochGasPrice} isStats inMIST />
								</Stats>
							</div>
						</div>
					</div>
				</Card>
			</div>
		</div>
	);
}
